import { Select as SelectPrimitive } from "@base-ui/react/select"
import { UnfoldMoreIcon } from "@hugeicons/core-free-icons"
import { HugeiconsIcon } from "@hugeicons/react"
import { Button } from "@unfiddle/ui/components/button"
import { Icons } from "@unfiddle/ui/components/icons"
import { MENU_ITEM_STYLES } from "@unfiddle/ui/components/menu/constants"
import { POPUP_STYLES } from "@unfiddle/ui/constants"
import { cn } from "../utils"

export const Select = SelectPrimitive.Root
export const SelectValue = SelectPrimitive.Value
export const SelectIcon = SelectPrimitive.Icon
export const SelectItemIndicator = SelectPrimitive.ItemIndicator
export const SelectItemText = SelectPrimitive.ItemText
export const SelectGroup = SelectPrimitive.Group
const SelectPortal = SelectPrimitive.Portal
const SelectBackdrop = SelectPrimitive.Backdrop
const SelectPositioner = SelectPrimitive.Positioner

export function SelectGroupLabel({
   className,
   children,
   ...props
}: React.ComponentProps<typeof SelectPrimitive.GroupLabel>) {
   return (
      <SelectPrimitive.GroupLabel
         className={cn(POPUP_STYLES.groupLabel, className)}
         {...props}
      >
         {children}
      </SelectPrimitive.GroupLabel>
   )
}

interface Props extends React.ComponentProps<typeof SelectPrimitive.Trigger> {}

export function SelectTrigger({ children, className, ...props }: Props) {
   return (
      <SelectPrimitive.Trigger
         onPointerDown={(e) => {
            e.stopPropagation()
         }}
         render={
            <Button
               variant={"secondary"}
               className={cn("flex justify-start text-sm", className)}
            >
               {children ?? (
                  <>
                     <SelectValue>{(v) => v}</SelectValue>
                     <SelectTriggerIcon />
                  </>
               )}
            </Button>
         }
         nativeButton
         {...props}
      >
         {children}
      </SelectPrimitive.Trigger>
   )
}

export function SelectTriggerIcon() {
   return (
      <SelectIcon
         data-icon
         className="-mr-1 ml-auto flex shrink-0"
      >
         <HugeiconsIcon
            icon={UnfoldMoreIcon}
            className="size-4.5 text-muted md:size-4"
         />
      </SelectIcon>
   )
}

interface ItemProps extends React.ComponentProps<typeof SelectPrimitive.Item> {
   destructive?: boolean
}

export function SelectItem({
   className,
   destructive = false,
   children,
   ...props
}: ItemProps) {
   return (
      <SelectPrimitive.Item
         className={cn(
            MENU_ITEM_STYLES.base,
            destructive ? MENU_ITEM_STYLES.destructive : "",
            "grid min-w-(--anchor-width) grid-cols-[1rem_1fr] items-center gap-2 group-data-[side=none]:min-w-[calc(var(--anchor-width)+1.4rem)]",
            className,
         )}
         {...props}
      >
         <SelectItemIndicator className="col-start-1">
            <HugeiconsIcon
               icon={Icons.check}
               strokeWidth={2}
               className={"-ml-0.5 size-5.5 text-white/90 md:size-5"}
            />
         </SelectItemIndicator>
         <SelectItemText className={"col-start-2"}>{children}</SelectItemText>
      </SelectPrimitive.Item>
   )
}

export function SelectPopup({
   className,
   children,
   sideOffset = 0,
   ...props
}: React.ComponentProps<typeof SelectPrimitive.Positioner>) {
   return (
      <SelectPortal>
         <SelectBackdrop />
         <SelectPositioner
            sideOffset={sideOffset}
            className={"group z-51"}
            {...props}
         >
            <SelectPrimitive.Popup
               className={cn(
                  POPUP_STYLES.base,
                  POPUP_STYLES.transition,
                  "scale-100! p-(--popup-padding) transition-none",
                  className,
               )}
            >
               {children}
            </SelectPrimitive.Popup>
         </SelectPositioner>
      </SelectPortal>
   )
}

export function SelectSeparator({
   className,
   ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
   return (
      <SelectPrimitive.Separator
         className={cn(POPUP_STYLES.separator, className)}
         {...props}
      />
   )
}
